import React from 'react';
import classNames from 'classnames';
import styles from './Link.module.scss';
import Element from '../Element/Element';
import useLinkAdapter from './useLinkAdapter';
import DefaultLinkAdapter from './DefaultLinkAdapter';
import { CommonProps } from '../Element/Element';
import { CreateProps } from '../../types/utils/CreateProps';

export type LinkProps = CreateProps<
  {
    /** link path */
    to?: string;
    /** set link to open in new window */
    openInNewWindow?: boolean;
    /** linkAdapter injects the custom link component */
    linkAdapter?: LinkAdapter;
    /** removes the attribute rel="noopener noreferrer"  */
    removeRelAttribute?: boolean;
  },
  CommonProps<'a'>,
  'href' | 'target'
>;
// Alias for backwards compatibility
export type LinkAttributes = LinkProps;

export type LinkAdapterProps = { className: string } & Omit<
  LinkProps,
  'linkAdapter' | 'className' | 'color' | 'hoverColor'
>;

export type LinkAdapter = (props: LinkAdapterProps) => React.ReactElement;

const Link: React.FunctionComponent<LinkProps> = ({
  children,
  openInNewWindow = false,
  color = 'purple500',
  hoverColor = 'purple400',
  linkAdapter,
  className,
  occupyHorizontalSpace = false,
  removeRelAttribute = false,
  ...props
}) => {
  // Figure out which adapter to use
  const providedAdapter = useLinkAdapter();
  const adapter = linkAdapter || providedAdapter || DefaultLinkAdapter;

  // Wrap the styled children using a interchangeable adapter
  return adapter({
    openInNewWindow,
    removeRelAttribute,
    className: classNames([styles.link, className]),
    ...props,
    children: (
      <Element
        color={color}
        hoverColor={hoverColor}
        tagName="span"
        occupyHorizontalSpace={occupyHorizontalSpace}
      >
        {children}
      </Element>
    ),
  });
};

export default Link;
